import React, { useMemo, useState } from "react";
import api from "../../../apiClient";

export default function AttachmentsSection({ data, onChange, packet, packetId }) {
  // Resolve packet id from either explicit prop or the whole packet object.
  const resolvedPacketId =
    packetId ??
    (packet?.id ?? packet?.packet_id ?? null);

  // Seed from server or draft
  const initial = Array.isArray(data?.attachments) ? data.attachments : [];
  const [items, setItems] = useState(initial);

  // Rehydrate only when server's copy changes (avoid stomping local draft)
  const serverHash = React.useMemo(
    () => JSON.stringify(data?.attachments || []),
    [data?.attachments]
  );
  const lastServerHashRef = React.useRef(serverHash);

  React.useEffect(() => {
    if (serverHash !== lastServerHashRef.current) {
      const latest = Array.isArray(data?.attachments) ? data.attachments : [];
      setItems(latest);
      lastServerHashRef.current = serverHash;
    }
  }, [serverHash, data?.attachments]);

  const [busy, setBusy] = useState(false);
  const nextName = useMemo(() => `Attachment ${items.length + 1}`, [items.length]);

  // Base URL for backend API (same one axios uses)
  const apiBase = (api?.defaults?.baseURL || "").replace(/\/+$/, "");

  // ---- helpers ----
  function dedupeByTitleKeepLast(arr) {
    const seen = new Set();
    const out = [];
    // keep last item for a given normalized title
    for (let i = arr.length - 1; i >= 0; i--) {
      const it = arr[i] || {};
      const key = String(it.title || "").trim().toLowerCase();
      if (!key || !seen.has(key)) {
        if (key) seen.add(key);
        out.unshift(it);
      }
    }
    return out;
  }

  function pushToDraft(next) {
    const deduped = dedupeByTitleKeepLast(next);
    setItems(deduped);
    onChange?.({ attachments: deduped });
  }

  // ---- mutations (all update draft immediately; top Save persists) ----
  function addItem() {
    const next = [
      ...items,
      {
        id: crypto?.randomUUID?.() || `att_${Math.random().toString(36).slice(2, 9)}`,
        title: nextName,
        url: "",
        mime: "",
        // internal key for secure backend access
        key: "",
        include: true,
        notes: "",
      },
    ];
    pushToDraft(next);
  }

  function update(i, k, v) {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    pushToDraft(next);
  }

  function remove(i) {
    const next = [...items];
    next.splice(i, 1);
    pushToDraft(next);
  }

  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    const t = next[i];
    next[i] = next[j];
    next[j] = t;
    pushToDraft(next);
  }

  async function uploadFile(idx, file) {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "attachment");
      fd.append("packet_id", String(resolvedPacketId || ""));
      const currentTitle = items[idx]?.title || file.name.replace(/\.[^.]+$/, "");
      fd.append("title", currentTitle);

      const res = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const url = res?.data?.url || res?.data?.publicUrl || res?.data?.public_url || "";
      const mime = res?.data?.mime || file.type || "";
      const key = res?.data?.key || "";

      if (!url) throw new Error("Upload did not return a URL");

      // Single atomic patch -> state + draft in lockstep
      setItems((prev) => {
        const next = [...prev];

        const existing = next[idx] || {};
        const row = {
          ...existing,
          title: currentTitle,
          url,
          mime,
          // Prefer fresh key from server; fall back to any existing key if present
          key: key || existing.key || "",
        };
        next[idx] = row;

        const deduped = dedupeByTitleKeepLast(next);
        onChange?.({ attachments: deduped }); // top Save will persist this
        return deduped;
      });
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  // Helper: derive a key from a legacy public URL if possible (for old packets).
  function keyFromLegacyUrl(url) {
    if (!url) return "";
    const idx = url.indexOf("/attachments/");
    if (idx === -1) return "";
    const suffix = url.slice(idx + 1); // keep "attachments/..."
    return suffix || "";
  }

  // Helper: given an attachment, compute the URL we actually open in the browser.
  // - If we have a key and an API base URL → go through secure backend endpoint
  // - Else if we can derive a key from the legacy URL → also use secure endpoint
  // - Else → fall back to the stored public URL (legacy packets before key support)
  function getOpenHref(att) {
    // Prefer the signed URL the server built. /files/secure/by-key requires a
    // time-limited signature, and a plain anchor href cannot send an
    // Authorization header, so the credential travels in the URL. Building it
    // here would produce an unsigned link that 403s once
    // FILES_REQUIRE_SIGNATURE is on.
    if (att?.signed_url) return att.signed_url;

    const legacyUrl = att?.url || "";
    const explicitKey = att?.key;

    // A freshly uploaded attachment already carries a signed url from
    // /files/upload, before the section has been re-read from the server.
    if (legacyUrl.includes("/files/secure/by-key") && legacyUrl.includes("sig=")) {
      return legacyUrl;
    }

    const effectiveKey = explicitKey || keyFromLegacyUrl(legacyUrl);

    // Legacy fallback: unsigned, so it only works while signatures are optional.
    if (effectiveKey && apiBase) {
      return `${apiBase}/files/secure/by-key?key=${encodeURIComponent(effectiveKey)}`;
    }
    return legacyUrl;
  }

  return (
    <div className="space-y-3">
      <div className="mb-3 flex items-center gap-2">
        <button
          onClick={addItem}
          className="rounded-lg px-3 py-2 border border-gray-200 bg-white hover:border-gray-300"
        >
          + Add Attachment
        </button>
        <span className="text-xs text-slate-500">
          Changes are staged. Use the main <b>Save</b> button at the top to persist.
        </span>
      </div>

      <div className="space-y-3">
        {items.map((it, i) => {
          const openHref = getOpenHref(it);
          return (
            <div key={it.id || i} className="rounded-xl border border-gray-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium">Attachment #{i + 1}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(i, -1)} className="rounded-md border px-2 py-1 text-xs">
                    ↑
                  </button>
                  <button onClick={() => move(i, 1)} className="rounded-md border px-2 py-1 text-xs">
                    ↓
                  </button>
                  <button
                    onClick={() => remove(i)}
                    className="rounded-md border border-red-200 text-red-600 px-2 py-1 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-2">
                <Field
                  label="Title (shown in Print)"
                  value={it.title || ""}
                  onChange={(v) => update(i, "title", v)}
                />
                <Field
                  label="URL (auto-fills after upload)"
                  value={it.url || ""}
                  onChange={(v) => update(i, "url", v)}
                />

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Upload file (PDF or image)</label>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => uploadFile(i, e.target.files?.[0])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    disabled={busy}
                  />
                  {openHref && (
                    <div className="mt-1 text-xs text-slate-500">
                      <a
                        href={openHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Open attachment
                      </a>
                      {(it.key || keyFromLegacyUrl(it.url)) && apiBase && (
                        <span className="ml-1">(via secure endpoint)</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id={`incl-${i}`}
                    type="checkbox"
                    checked={!!it.include}
                    onChange={(e) => update(i, "include", e.target.checked)}
                  />
                  <label htmlFor={`incl-${i}`} className="text-sm">
                    Include (default)
                  </label>
                </div>

                <div className="sm:col-span-4">
                  <label className="block text-xs text-gray-500 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={it.notes || ""}
                    onChange={(e) => update(i, "notes", e.target.value)}
                  />
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="text-sm text-slate-500">No attachments yet.</div>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2"
      />
    </div>
  );
}
